import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config/config';
import { useToast } from './common/ToastContext';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';
import './SecurityAgent.css';

/* ── Severity palette ─────────────────────────────────────────────────────── */
const SEV = {
  critical: { color: '#d32f2f', bg: 'rgba(211,47,47,.10)', label: 'Critical' },
  high:     { color: '#f57c00', bg: 'rgba(245,124,0,.10)',  label: 'High' },
  medium:   { color: '#fbc02d', bg: 'rgba(251,192,45,.12)', label: 'Medium' },
  low:      { color: '#1976d2', bg: 'rgba(25,118,210,.10)', label: 'Low' },
};

const STAGES = ['scanning', 'triaging', 'acting', 'done'];

const ACTION_TYPE_LABELS = {
  remediation_create: 'Remediation',
  vex_propose: 'VEX',
  alert_send: 'Alert',
  suppress: 'Suppress',
};

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  auto_approved: 'Auto-approved',
  auto_escalated: 'Escalated',
};

/* ── Helpers ──────────────────────────────────────────────────────────────── */
function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = (ms / 1000).toFixed(1);
  return s < 60 ? `${s}s` : `${(ms / 60000).toFixed(1)}m`;
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function SecurityAgent() {
  const { t } = useTranslation();
  const { showToast } = useToast();

  /* ── State ─────────────────────────────────────────────────────────────── */
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);          // /agent/status
  const [actions, setActions] = useState([]);           // /agent/actions
  const [runs, setRuns] = useState([]);                 // /agent/runs
  const [agentConfig, setAgentConfig] = useState(null); // /agent/config
  const [configDirty, setConfigDirty] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [triggeringRun, setTriggeringRun] = useState(false);
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);

  // Filters
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRunId, setFilterRunId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // UI panels
  const [showHistory, setShowHistory] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const pollRef = useRef(null);

  /* ── Data fetching ─────────────────────────────────────────────────────── */
  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.BASE_URL}/agent/status`);
      setStatus(data);
      return data;
    } catch { return null; }
  }, []);

  const fetchActions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.BASE_URL}/agent/actions`);
      setActions(data.actions || []);
    } catch { /* silent */ }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.BASE_URL}/agent/runs`);
      setRuns(data.runs || []);
    } catch { /* silent */ }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.BASE_URL}/agent/config`);
      setAgentConfig(data);
    } catch { /* silent */ }
  }, []);

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchStatus(), fetchActions(), fetchRuns(), fetchConfig()]);
    setLoading(false);
  }, [fetchStatus, fetchActions, fetchRuns, fetchConfig]);

  /* ── Initial load ──────────────────────────────────────────────────────── */
  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── Polling during active run ─────────────────────────────────────────── */
  const isRunning = status?.activeRun && !['done', 'failed'].includes(status.activeRun.status);

  useEffect(() => {
    if (isRunning) {
      pollRef.current = setInterval(async () => {
        const s = await fetchStatus();
        if (s?.activeRun && ['done', 'failed'].includes(s.activeRun.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          fetchActions();
          fetchRuns();
          if (s.activeRun.status === 'done') {
            showToast(t('securityAgent.runComplete'), 'success');
          } else {
            showToast(s.activeRun.error || t('securityAgent.runFailed'), 'error');
          }
        }
      }, 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isRunning, fetchStatus, fetchActions, fetchRuns, showToast, t]);

  /* ── Trigger run ───────────────────────────────────────────────────────── */
  const triggerRun = async () => {
    setTriggeringRun(true);
    try {
      await axios.post(`${config.BASE_URL}/agent/run`);
      showToast(t('securityAgent.runStarted'), 'success');
      await fetchStatus();
    } catch (e) {
      showToast(e.response?.data?.error || t('securityAgent.runError'), 'error');
    } finally { setTriggeringRun(false); }
  };

  /* ── Approve / reject ──────────────────────────────────────────────────── */
  const approveAction = async (id) => {
    try {
      await axios.post(`${config.BASE_URL}/agent/actions/${id}/approve`);
      setActions(prev => prev.map(a => a.id === id ? { ...a, status: 'approved' } : a));
      showToast(t('securityAgent.actionApproved'), 'success');
    } catch { showToast(t('securityAgent.actionError'), 'error'); }
  };

  const rejectAction = async (id) => {
    try {
      await axios.post(`${config.BASE_URL}/agent/actions/${id}/reject`);
      setActions(prev => prev.map(a => a.id === id ? { ...a, status: 'rejected' } : a));
      showToast(t('securityAgent.actionRejected'), 'success');
    } catch { showToast(t('securityAgent.actionError'), 'error'); }
  };

  /* ── Bulk operations ───────────────────────────────────────────────────── */
  const bulkApprove = async () => {
    setBulkWorking(true);
    const ids = [...selectedActions];
    try {
      await Promise.all(ids.map(id => axios.post(`${config.BASE_URL}/agent/actions/${id}/approve`)));
      setActions(prev => prev.map(a => ids.includes(a.id) ? { ...a, status: 'approved' } : a));
      setSelectedActions(new Set());
      showToast(t('securityAgent.bulkApproved', { count: ids.length }), 'success');
    } catch { showToast(t('securityAgent.actionError'), 'error'); }
    setBulkWorking(false);
  };

  const bulkReject = async () => {
    setBulkWorking(true);
    const ids = [...selectedActions];
    try {
      await Promise.all(ids.map(id => axios.post(`${config.BASE_URL}/agent/actions/${id}/reject`)));
      setActions(prev => prev.map(a => ids.includes(a.id) ? { ...a, status: 'rejected' } : a));
      setSelectedActions(new Set());
      showToast(t('securityAgent.bulkRejected', { count: ids.length }), 'success');
    } catch { showToast(t('securityAgent.actionError'), 'error'); }
    setBulkWorking(false);
  };

  /* ── Save config ───────────────────────────────────────────────────────── */
  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${config.BASE_URL}/agent/config`, agentConfig);
      setConfigDirty(false);
      showToast(t('securityAgent.configSaved'), 'success');
    } catch { showToast(t('securityAgent.configError'), 'error'); }
    setSavingConfig(false);
  };

  const updateConfig = (key, val) => {
    setAgentConfig(prev => ({ ...prev, [key]: val }));
    setConfigDirty(true);
  };

  /* ── Filtering & pagination ────────────────────────────────────────────── */
  const filteredActions = actions.filter(a => {
    if (filterSeverity !== 'all' && a.severity?.toLowerCase() !== filterSeverity) return false;
    if (filterType !== 'all' && a.actionType !== filterType) return false;
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    if (filterRunId !== 'all' && a.runId !== filterRunId) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (a.cveId?.toLowerCase().includes(q) ||
              a.packageName?.toLowerCase().includes(q) ||
              a.sbomName?.toLowerCase().includes(q));
    }
    return true;
  });

  const nPages = Math.ceil(filteredActions.length / pageSize);
  const pageActions = filteredActions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filterSeverity, filterType, filterStatus, filterRunId, searchTerm]);

  /* ── Selection helpers ─────────────────────────────────────────────────── */
  const toggleSelect = (id) => {
    setSelectedActions(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pendingOnPage = pageActions.filter(a => a.status === 'pending').map(a => a.id);
    const allSelected = pendingOnPage.every(id => selectedActions.has(id));
    if (allSelected) {
      setSelectedActions(prev => {
        const next = new Set(prev);
        pendingOnPage.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedActions(prev => {
        const next = new Set(prev);
        pendingOnPage.forEach(id => next.add(id));
        return next;
      });
    }
  };

  /* ── Active run helpers ────────────────────────────────────────────────── */
  const activeRun = status?.activeRun;
  const lastRun = status?.lastRun;
  const stageIdx = activeRun ? STAGES.indexOf(activeRun.status) : -1;

  /* ── Loading state ─────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="sa-page">
        <div className="sa-hero">
          <Skeleton width="40%" height={32} />
          <Skeleton width="20%" height={18} style={{ marginTop: 8 }} />
        </div>
        <Skeleton.Table rows={8} cols={6} />
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════════════════
   *  RENDER
   * ══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="sa-page">
      {/* ── Hero Header ────────────────────────────────────────────────── */}
      <header className="sa-hero">
        <div className="sa-hero-left">
          <h1 className="sa-title">
            <span className="sa-title-icon">🛡️</span>
            {t('securityAgent.title')}
          </h1>
          <p className="sa-subtitle">{t('securityAgent.subtitle')}</p>
        </div>
        <div className="sa-hero-right">
          <span className={`sa-status-badge sa-status-${isRunning ? 'active' : agentConfig?.enabled ? 'idle' : 'disabled'}`}>
            <span className="sa-status-dot" />
            {isRunning ? t('securityAgent.statusActive') : agentConfig?.enabled ? t('securityAgent.statusIdle') : t('securityAgent.statusDisabled')}
          </span>
        </div>
      </header>

      {/* ── Control Panel ──────────────────────────────────────────────── */}
      <section className="sa-control-panel">
        <div className="sa-control-left">
          <button
            className="sa-run-btn ce-btn ce-btn--primary"
            onClick={triggerRun}
            disabled={isRunning || triggeringRun}
          >
            {isRunning ? (
              <><span className="sa-spinner" /> {t('securityAgent.running')}</>
            ) : triggeringRun ? (
              <><span className="sa-spinner" /> {t('securityAgent.starting')}</>
            ) : (
              <>{t('securityAgent.runAgent')}</>
            )}
          </button>
          <div className="sa-control-status">
            {isRunning ? (
              <span className="sa-live-text">
                {activeRun.status === 'scanning' && `${t('securityAgent.scanning')} (${activeRun.sbomsScanned || 0})`}
                {activeRun.status === 'triaging' && `${t('securityAgent.triaging')} — ${activeRun.newCves || 0} CVEs`}
                {activeRun.status === 'acting' && t('securityAgent.acting')}
              </span>
            ) : lastRun ? (
              <span className="sa-idle-text">
                {t('securityAgent.lastRunAt', { time: timeAgo(lastRun.completedAt || lastRun.startedAt) })}
              </span>
            ) : (
              <span className="sa-idle-text">{t('securityAgent.noRunsYet')}</span>
            )}
          </div>
        </div>

        {/* Quick stats */}
        {(lastRun || activeRun) && (
          <div className="sa-quick-stats ce-kpi-row">
            {[
              { label: t('securityAgent.statScanned'), value: (activeRun || lastRun).sbomsScanned || 0, icon: '📦' },
              { label: t('securityAgent.statCves'), value: (activeRun || lastRun).newCves || 0, icon: '🔍' },
              { label: t('securityAgent.statActions'), value: (activeRun || lastRun).actionsCreated || 0, icon: '⚡' },
              { label: t('securityAgent.statSuppressed'), value: (activeRun || lastRun).suppressed || 0, icon: '🔇' },
            ].map(s => (
              <div key={s.label} className="sa-stat-card ce-kpi">
                <div className="ce-kpi-value">{s.icon} {s.value}</div>
                <div className="ce-kpi-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Progress bar (during active run) ───────────────────────────── */}
      {isRunning && activeRun && (
        <section className="sa-progress-section">
          <div className="sa-progress-stages">
            {STAGES.map((stage, i) => (
              <div key={stage} className={`sa-stage ${i < stageIdx ? 'complete' : i === stageIdx ? 'active' : ''}`}>
                <div className="sa-stage-dot">
                  {i < stageIdx ? '✓' : i === stageIdx ? <span className="sa-stage-pulse" /> : (i + 1)}
                </div>
                <span className="sa-stage-label">{t(`securityAgent.stage_${stage}`)}</span>
              </div>
            ))}
          </div>
          <div className="sa-progress-bar-track">
            <div
              className="sa-progress-bar-fill"
              style={{ width: `${Math.max(((stageIdx + 1) / STAGES.length) * 100, 5)}%` }}
            />
          </div>
          <div className="sa-progress-counters">
            <span>{t('securityAgent.statScanned')}: <strong>{activeRun.sbomsScanned || 0}</strong></span>
            <span>{t('securityAgent.statCves')}: <strong>{activeRun.newCves || 0}</strong></span>
            <span>{t('securityAgent.statActions')}: <strong>{activeRun.actionsCreated || 0}</strong></span>
            <span>VEX: <strong>{activeRun.vexProposed || 0}</strong></span>
            <span>{t('securityAgent.alerts')}: <strong>{activeRun.alertsSent || 0}</strong></span>
          </div>
        </section>
      )}

      {/* ── Action Queue ───────────────────────────────────────────────── */}
      <section className="sa-actions-section">
        <div className="sa-section-header ce-section-header">
          <h2 className="ce-section-title">{t('securityAgent.actionQueue')}</h2>
          <span className="ce-sev ce-sev--info">{filteredActions.length}</span>
        </div>

        {/* Filters */}
        <div className="sa-filters ce-filter-bar">
          <input
            className="sa-search ce-search-input"
            type="text"
            placeholder={t('securityAgent.searchPlaceholder')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <select className="sa-filter-select ce-filter-select" value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}>
            <option value="all">{t('securityAgent.allSeverities')}</option>
            {Object.keys(SEV).map(s => <option key={s} value={s}>{SEV[s].label}</option>)}
          </select>
          <select className="sa-filter-select ce-filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">{t('securityAgent.allTypes')}</option>
            {Object.entries(ACTION_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="sa-filter-select ce-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">{t('securityAgent.allStatuses')}</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {filterRunId !== 'all' && (
            <button className="sa-filter-clear ce-btn ce-btn--sm" onClick={() => setFilterRunId('all')}>
              ✕ {t('securityAgent.clearRunFilter')}
            </button>
          )}
        </div>

        {/* Bulk bar */}
        {selectedActions.size > 0 && (
          <div className="sa-bulk-bar">
            <span>{t('securityAgent.selected', { count: selectedActions.size })}</span>
            <button className="sa-bulk-approve ce-btn ce-btn--primary ce-btn--sm" onClick={bulkApprove} disabled={bulkWorking}>
              ✓ {t('securityAgent.approveSelected')}
            </button>
            <button className="sa-bulk-reject ce-btn ce-btn--danger ce-btn--sm" onClick={bulkReject} disabled={bulkWorking}>
              ✕ {t('securityAgent.rejectSelected')}
            </button>
            <button className="sa-bulk-clear ce-btn ce-btn--secondary ce-btn--sm" onClick={() => setSelectedActions(new Set())}>
              {t('securityAgent.clearSelection')}
            </button>
          </div>
        )}

        {/* Table */}
        {filteredActions.length === 0 ? (
          actions.length === 0 ? (
            <EmptyState
              icon="🛡️"
              title={t('securityAgent.emptyTitle')}
              description={t('securityAgent.emptyDesc')}
              action={{ label: t('securityAgent.runAgent'), onClick: triggerRun }}
            />
          ) : (
            <EmptyState
              icon="🔍"
              title={t('securityAgent.noMatch')}
              description={t('securityAgent.noMatchDesc')}
            />
          )
        ) : (
          <>
            <div className="sa-table-wrap ce-table-wrap">
              <table className="sa-table ce-table">
                <thead>
                  <tr>
                    <th className="sa-th-check">
                      <input
                        type="checkbox"
                        checked={pageActions.filter(a => a.status === 'pending').length > 0 &&
                                 pageActions.filter(a => a.status === 'pending').every(a => selectedActions.has(a.id))}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>{t('securityAgent.colSeverity')}</th>
                    <th>{t('securityAgent.colCve')}</th>
                    <th>{t('securityAgent.colPackage')}</th>
                    <th>{t('securityAgent.colSbom')}</th>
                    <th>{t('securityAgent.colType')}</th>
                    <th>{t('securityAgent.colConfidence')}</th>
                    <th>{t('securityAgent.colStatus')}</th>
                    <th>{t('securityAgent.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageActions.map(a => {
                    return (
                      <tr key={a.id} className={selectedActions.has(a.id) ? 'sa-row-selected' : ''}>
                        <td className="sa-td-check">
                          {a.status === 'pending' && (
                            <input
                              type="checkbox"
                              checked={selectedActions.has(a.id)}
                              onChange={() => toggleSelect(a.id)}
                            />
                          )}
                        </td>
                        <td>
                          <span className={`ce-sev ce-sev--${(a.severity || 'low').toLowerCase()}`}>
                            {a.severity || 'Unknown'}
                          </span>
                        </td>
                        <td className="sa-td-cve">{a.cveId || '—'}</td>
                        <td className="sa-td-pkg" title={a.packageName}>{a.packageName || '—'}</td>
                        <td className="sa-td-sbom" title={a.sbomName}>{a.sbomName || '—'}</td>
                        <td>
                          <span className={`sa-type-badge sa-type-${a.actionType}`}>
                            {ACTION_TYPE_LABELS[a.actionType] || a.actionType}
                          </span>
                        </td>
                        <td>
                          {a.confidence != null ? (
                            <span className="sa-confidence">{Math.round(a.confidence * 100)}%</span>
                          ) : '—'}
                        </td>
                        <td>
                          <span className={`ce-status ce-status--${a.status === 'pending' ? 'open' : a.status === 'approved' || a.status === 'auto_approved' ? 'resolved' : a.status === 'rejected' ? 'false-pos' : 'in-progress'}`}>
                            {STATUS_LABELS[a.status] || a.status}
                          </span>
                        </td>
                        <td className="sa-td-actions">
                          {a.status === 'pending' ? (
                            <>
                              <button className="sa-act-approve ce-btn ce-btn--primary ce-btn--sm" onClick={() => approveAction(a.id)} title={t('securityAgent.approve')}>✓</button>
                              <button className="sa-act-reject ce-btn ce-btn--danger ce-btn--sm" onClick={() => rejectAction(a.id)} title={t('securityAgent.reject')}>✕</button>
                            </>
                          ) : (
                            <span className="sa-act-done">—</span>
                          )}
                          {a.reasoning && (
                            <span className="sa-reasoning-tip" title={a.reasoning}>💡</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              nPages={nPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={(s) => { setPageSize(s); setCurrentPage(1); }}
              totalItems={filteredActions.length}
            />
          </>
        )}
      </section>

      {/* ── Run History ────────────────────────────────────────────────── */}
      <section className="sa-history-section">
        <button className="sa-section-toggle" onClick={() => setShowHistory(h => !h)}>
          <h2>{t('securityAgent.runHistory')}</h2>
          <span className={`sa-chevron ${showHistory ? 'open' : ''}`}>›</span>
        </button>
        {showHistory && (
          runs.length === 0 ? (
            <div className="sa-history-empty">{t('securityAgent.noRuns')}</div>
          ) : (
            <div className="sa-table-wrap ce-table-wrap">
              <table className="sa-table sa-table-history ce-table">
                <thead>
                  <tr>
                    <th>{t('securityAgent.colRunId')}</th>
                    <th>{t('securityAgent.colTrigger')}</th>
                    <th>{t('securityAgent.colDuration')}</th>
                    <th>{t('securityAgent.colStatus')}</th>
                    <th>{t('securityAgent.statScanned')}</th>
                    <th>{t('securityAgent.statCves')}</th>
                    <th>{t('securityAgent.statActions')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.id}>
                      <td className="sa-td-runid">{r.id}</td>
                      <td>{r.triggerType || 'manual'}</td>
                      <td>{fmtDuration(r.duration)}</td>
                      <td>
                        <span className={`ce-status ce-status--${r.status === 'done' ? 'resolved' : r.status === 'failed' ? 'false-pos' : 'in-progress'}`}>{r.status}</span>
                      </td>
                      <td>{r.sbomsScanned || 0}</td>
                      <td>{r.newCves || 0}</td>
                      <td>{r.actionsCreated || 0}</td>
                      <td>
                        <button
                          className="sa-history-filter-btn"
                          onClick={() => { setFilterRunId(r.id); setShowHistory(false); }}
                          title={t('securityAgent.filterByRun')}
                        >🔍</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>

      {/* ── Configuration Panel ────────────────────────────────────────── */}
      <section className="sa-config-section">
        <button className="sa-section-toggle" onClick={() => setShowConfig(c => !c)}>
          <h2>{t('securityAgent.configuration')}</h2>
          <span className={`sa-chevron ${showConfig ? 'open' : ''}`}>›</span>
        </button>
        {showConfig && agentConfig && (
          <div className="sa-config-grid">
            <label className="sa-config-row">
              <span className="sa-config-label">{t('securityAgent.cfgEnabled')}</span>
              <span className="sa-config-desc">{t('securityAgent.cfgEnabledDesc')}</span>
              <input type="checkbox" className="sa-toggle" checked={!!agentConfig.enabled}
                onChange={e => updateConfig('enabled', e.target.checked)} />
            </label>
            <label className="sa-config-row">
              <span className="sa-config-label">{t('securityAgent.cfgAutoSuppress')}</span>
              <span className="sa-config-desc">{t('securityAgent.cfgAutoSuppressDesc')}</span>
              <input type="checkbox" className="sa-toggle" checked={!!agentConfig.autoApproveSuppress}
                onChange={e => updateConfig('autoApproveSuppress', e.target.checked)} />
            </label>
            <label className="sa-config-row">
              <span className="sa-config-label">{t('securityAgent.cfgAutoEscalate')}</span>
              <span className="sa-config-desc">{t('securityAgent.cfgAutoEscalateDesc')}</span>
              <input type="checkbox" className="sa-toggle" checked={!!agentConfig.autoEscalateCritical}
                onChange={e => updateConfig('autoEscalateCritical', e.target.checked)} />
            </label>
            <label className="sa-config-row">
              <span className="sa-config-label">{t('securityAgent.cfgAlertCritical')}</span>
              <span className="sa-config-desc">{t('securityAgent.cfgAlertCriticalDesc')}</span>
              <input type="checkbox" className="sa-toggle" checked={!!agentConfig.alertOnNewCritical}
                onChange={e => updateConfig('alertOnNewCritical', e.target.checked)} />
            </label>
            <div className="sa-config-row">
              <span className="sa-config-label">{t('securityAgent.cfgSchedule')}</span>
              <span className="sa-config-desc">{t('securityAgent.cfgScheduleDesc')}</span>
              <select className="sa-config-select" value={agentConfig.scheduleInterval || ''}
                onChange={e => updateConfig('scheduleInterval', e.target.value)}>
                <option value="">{t('securityAgent.schedDisabled')}</option>
                <option value="hourly">{t('securityAgent.schedHourly')}</option>
                <option value="daily">{t('securityAgent.schedDaily')}</option>
                <option value="weekly">{t('securityAgent.schedWeekly')}</option>
              </select>
            </div>
            <div className="sa-config-actions">
              <button className="sa-save-btn ce-btn ce-btn--primary" onClick={saveConfig} disabled={!configDirty || savingConfig}>
                {savingConfig ? t('securityAgent.saving') : t('securityAgent.saveConfig')}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
